import { DeleteIcon } from "@chakra-ui/icons";
import { Box, Button, Flex, Grid, Heading, Image, Spacer, Text } from "@chakra-ui/react";
import { ReactNode, createContext, useState } from "react";
import { useLocation } from "wouter";

import { XYLocation } from "@nens/deck-gl";

import { MainForm } from "@/features/main-form/components/main-form";
import { MainFormData } from "@/features/main-form/types/main-form-data";
import { ActualProjectCreateRequest, useCreateProject } from "@/queries/mutate-project";

import { StepDisplay } from "../components/step-display";
import { GeocoderInput } from "../features/geocoder-input/components/geocoder-input";
import { MainMap } from "../features/main-map/components/main-map";

interface ProjectConfigurationContext {
  location: {
    polygon: XYLocation[];
    address?: string;
  } | null;
  setPolygon: (polygon: XYLocation[], zoomTo?: boolean, address?: string) => void;
  clearPolygon: () => void;
}

export const ProjectConfigurationContext = createContext<ProjectConfigurationContext>({
  location: null,
  setPolygon: () => undefined,
  clearPolygon: () => undefined,
});

export function MainPage() {
  const [, navigate] = useLocation();

  const { mutateAsync: createProject, isPending } = useCreateProject();

  const [location, setLocation] = useState<ProjectConfigurationContext["location"]>(null);
  const [step, setStep] = useState(1);

  const setPolygon = (polygon: XYLocation[]) => {
    if (step === 1) {
      setLocation({ polygon });
    }
  };
  const clearPolygon = () => {
    setLocation(null);
    setStep(1);
  };

  const startCalculation = async (formData: MainFormData) => {
    if (!location) return;

    const { name, ...extraValues } = formData;

    const project: ActualProjectCreateRequest["project"] = {
      name,
      area: { type: "Polygon", coordinates: [[...location.polygon, location.polygon[0]]] },
      extraValues,
    };

    const { slug } = await createProject({ project });
    if (slug) navigate(`/reports/${slug}`);
  };

  return (
    <ProjectConfigurationContext.Provider
      value={{
        location,
        setPolygon,
        clearPolygon,
      }}
    >
      <Grid
        w="100vw"
        h="100vh"
        padding={0}
        gap={4}
        bg="brand.primary"
        templateColumns="1fr 1fr"
        position="relative"
      >
        <Box bg="brand.page_bg" id="main_map" p={0} m={0}>
          <MainMap />
        </Box>
        <Flex
          flexDirection="column"
          bg="brand.page_bg"
          position="relative"
          p={0}
          m={0}
          h="100%"
          overflow="auto"
          sx={{ ".full-height-form": { display: "flex", flexDir: "column", height: "100%" } }}
        >
          <Flex p={16} pt={12} pb={0} justifyContent="space-between">
            <Image alt="klimaatscan logo" h="62px" src="/assets/images/klimaatscan-logo.png" />
          </Flex>

          <Flex h="100%" flexDirection="column" p={16} py={12} gap={4}>
            {step === 1 && (
              <>
                <Heading as="h2" size="h4">
                  Gebied kiezen
                </Heading>
                <Text>Teken een gebied op de kaart of vul handmatig een adres in.</Text>
                <Text>
                  <Text as="strong">Let op:</Text> Teken het projectgebied zo nauwkeurig mogelijk
                  in. Indien er voor de berekening een buffer nodig is, wordt deze automatisch
                  toegevoegd.
                </Text>
                <GeocoderInput w="60%" />
                {location ? (
                  <Flex flexWrap="wrap" justifyContent="space-between" alignItems="center">
                    <Flex mr={4}>
                      <Heading as="h2" fontSize="150%">
                        Gebied gekozen ✓
                      </Heading>
                      {location.address ? (
                        <Text fontWeight="semibold">{location.address}</Text>
                      ) : null}
                    </Flex>
                    <Button
                      variant="danger"
                      onClick={clearPolygon}
                      w="fit-content"
                      leftIcon={<DeleteIcon />}
                    >
                      Verwijderen
                    </Button>
                  </Flex>
                ) : null}
                <ButtonWrapper>
                  <Button
                    ml="auto"
                    isDisabled={!location}
                    variant="primary"
                    onClick={() => setStep((step) => step + 1)}
                  >
                    Volgende
                  </Button>
                </ButtonWrapper>
              </>
            )}
            {step === 2 && (
              <MainForm
                className="full-height-form"
                onSubmit={({ formData }) => {
                  if (formData) startCalculation(formData);
                }}
              >
                <ButtonWrapper>
                  <Button
                    variant="secondary"
                    onClick={() => setStep((step) => Math.max(1, step - 1))}
                  >
                    Vorige
                  </Button>

                  <Button isLoading={isPending} ml="auto" type="submit" variant="primary">
                    Genereer rapportage
                  </Button>
                </ButtonWrapper>
              </MainForm>
            )}
          </Flex>
        </Flex>
        <StepDisplay step={step} />
      </Grid>
    </ProjectConfigurationContext.Provider>
  );
}
const ButtonWrapper = ({ children }: { children: ReactNode }) => {
  return (
    <>
      <Spacer />
      <Flex flexDirection="row" mt={16} gap={4}>
        {/* FUTURE */}
        {/* <Button
          isDisabled
          title="De logout knop bestaat nog niet"
          variant="secondary"
          onClick={() => {
            throw new Error("Logout not implemented yet.");
          }}
        >
          <CiLogout />
        </Button> */}
        {children}
      </Flex>
    </>
  );
};
